import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { IconButton, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useEffect, useState } from "react";
import { AiMappingDrawSession } from "../useAiMappingController";

interface AiMappingDrawFormProps {
  session: AiMappingDrawSession;
  saving: boolean;
  error: string | null;
  name: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}

export default function AiMappingDrawForm({
  session,
  saving,
  error,
  name,
  onSave,
  onCancel,
}: AiMappingDrawFormProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    setEditingName(false);
    setNameDraft("");
  }, [session]);

  const drawn = session.points !== null;
  const effectiveName = nameDraft.trim() || name;

  return (
    <MDBox display="flex" flexDirection="column" gap={1} data-testid="ai-mapping-draw-form">
      <MDTypography variant="button" fontWeight="medium">
        {session.inputType === "BBOX" ? "Draw a rectangle" : "Draw a polygon"}
      </MDTypography>
      <MDTypography variant="body2" color="text">
        {drawn
          ? "Section selected. Save it to add it to the run."
          : session.inputType === "BBOX"
          ? "Drag on the map to select a rectangular section."
          : "Click on the map to add polygon points; double-click to finish."}
      </MDTypography>

      {drawn && !editingName && (
        <MDBox display="flex" alignItems="center" gap={0.5} data-testid="ai-mapping-save-as">
          <MDTypography variant="caption" color="text">
            Will be saved as: {effectiveName}
          </MDTypography>
          <IconButton
            size="small"
            onClick={() => {
              setNameDraft(name);
              setEditingName(true);
            }}
            data-testid="ai-mapping-edit-name"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </MDBox>
      )}

      {drawn && editingName && (
        <TextField
          label="Measurement name"
          value={nameDraft}
          onChange={(event) => setNameDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") setEditingName(false);
          }}
          size="small"
          fullWidth
          disabled={saving}
          autoFocus
          inputProps={{ "data-testid": "ai-mapping-area-name" }}
        />
      )}

      {error && (
        <MDTypography variant="caption" color="error" data-testid="ai-mapping-form-error">
          {error}
        </MDTypography>
      )}

      <MDBox display="flex" gap={1}>
        <MDButton
          color="dark"
          variant="outlined"
          size="small"
          fullWidth
          onClick={onCancel}
          disabled={saving}
          data-testid="ai-mapping-draw-cancel"
        >
          Cancel
        </MDButton>
        <MDButton
          color="info"
          variant="gradient"
          size="small"
          fullWidth
          onClick={() => onSave(effectiveName)}
          disabled={!drawn || saving}
          startIcon={<SaveIcon />}
          data-testid="ai-mapping-draw-save"
        >
          {saving ? "Saving…" : "Save"}
        </MDButton>
      </MDBox>
    </MDBox>
  );
}
